import { connectSupport } from './liveSupportTransport';
import { PRIVATE_SELECTOR, createSupportSanitizer, executeNavigation } from './supportPrivacy';
const BASE = '/support/cobrowse';

export function createCobrowseSession({ api, onChange, onConsent, loadRecorder = () => import('rrweb'), connect = connectSupport }) {
  const sanitizeSupportEvent = createSupportSanitizer();
  let request, transport, stopRecording, recorder, closed = false, starting = false, controlled = false, localControl = false;
  let prompt, recordingPromise, expiry, latest = {}, generation = 0;
  const active = () => !closed && request && Date.now() < request.expiresAt;
  const post = event => api.post(`${BASE}/requests/${request.requestId}/events`, { event }, { timeout: 5000 });
  const publish = () => onChange({ status: latest.view ? 'active' : 'waiting', code: request.code, expiresAt: request.expiresAt, controlled });
  async function stop(message) {
    if (closed) return;
    closed = true; generation++; controlled = false; clearTimeout(expiry);
    stopRecording?.(); stopRecording = null; onConsent(null); prompt = null;
    transport?.close(); transport = null;
    onChange({ status: 'ended', message: typeof message === 'string' ? message : undefined });
    if (request) { try { await post('end'); } catch { /* Local disconnect and server lease expiry revoke access. */ } }
  }
  async function startRecording() {
    if (recordingPromise || stopRecording) return;
    recordingPromise = (async () => {
      recorder = await loadRecorder();
      if (!active() || !latest.view) return;
      stopRecording = recorder.record({ emit: event => {
        if (active() && latest.view) transport?.send({ type: 'frame', event: sanitizeSupportEvent(event) });
      }, blockSelector: PRIVATE_SELECTOR, maskAllInputs: true, inlineStylesheet: true,
      slimDOMOptions: 'all', recordCanvas: false, recordCrossOriginIframes: false,
      sampling: { mousemove: 100, scroll: 150, input: 'last' }, checkoutEveryNms: 60000 });
      if (!stopRecording) throw new Error('Recording unavailable');
    })().catch(() => stop('The shared view could not start. Please try a new session.'));
    await recordingPromise;
  }
  function consent(kind) {
    if (prompt === kind) return;
    prompt = kind;
    onConsent({ kind, agentName: 'AraSync support', respond: async allow => {
      if (!active() || prompt !== kind) return;
      prompt = null; onConsent(null);
      const expected = generation;
      try {
        const response = await post(allow ? `allow-${kind}` : 'decline');
        if (active() && expected === generation) { if (kind === 'control') localControl = allow; update(response.data.data); }
      } catch { await stop('Approval could not be confirmed. Please start a new session.'); }
    } });
  }
  function update(state) {
    if (!active()) return;
    if (state.ended) { void stop(); return; }
    latest = state; controlled = Boolean(localControl && state.control && state.view);
    if (state.pending) consent(state.pending);
    else if (prompt) { prompt = null; onConsent(null); }
    if (state.view) void startRecording();
    publish();
  }
  return {
    async start() {
      if (starting || closed) return;
      starting = true; onChange({ status: 'starting' });
      try {
        const response = await api.post(`${BASE}/requests`, { consent: true });
        request = response.data.data;
        if (closed) { await post('end'); return; }
        expiry = setTimeout(() => stop('The support session expired.'), Math.max(1, request.expiresAt - Date.now()));
        transport = await connect({ api, base: BASE, id: request.requestId,
          onClose: () => stop('The support connection ended. Start a new session to reconnect.'),
          onMessage: message => {
            if (!active()) return;
            if (message.type === 'state') update(message.state);
            if (message.type === 'command' && controlled && latest.view) executeNavigation(message, recorder?.record?.mirror);
          } });
        if (closed) { transport.close(); return; }
        publish();
      } catch { await stop('Live support could not connect. Please try again.'); }
    },
    async revokeControl() {
      controlled = false; localControl = false; generation++; latest.control = false; publish();
      try { await post('revoke-control'); }
      catch { await stop('Navigation stopped. The connection also ended because approval could not be updated.'); }
    },
    stop,
  };
}
